import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import {
  type GraphQLCommitment,
  GraphQLCommitmentType,
  type GraphQLSupporterBeneficiaryExit,
  type GraphQLSupporterGiftLimits,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { FinalGiftIneligibilityCode } from '@sola/shared-utils/my/gifts-type-guards.ts';

import type { ChildGiftOption } from '../../types/gifts.ts';
import { applyChildCartAwareness } from './gift-availability-cart.ts';
import { resolveGiftOption } from './gift-availability-rules.ts';

/**
 * Build a minimal GraphQLCommitment for an exited beneficiary so the
 * existing GiftCommitmentCard / dialog flow can render their Final gift.
 *
 * Only fields the Final flow reads are populated meaningfully; the rest
 * are safe defaults that satisfy the schema. The synthetic commitment is
 * scoped to the Final list — it never reaches sections that depend on
 * payment plans, recurring rates, etc.
 */
const synthesizeExitedCommitment = (
  exit: GraphQLSupporterBeneficiaryExit,
): GraphQLCommitment => ({
  globalId: exit.exitedBeneficiary.globalId,
  internalId: exit.exitedBeneficiary.commitmentInternalId,
  commitmentType: GraphQLCommitmentType.ChildSponsorship,
  participant: {
    globalId: exit.exitedBeneficiary.globalId,
    name: exit.exitedBeneficiary.displayName ?? null,
    names: { preferred: exit.exitedBeneficiary.preferredName ?? null },
    photoUrl: exit.exitedBeneficiary.fullBodyImageUrl ?? null,
    birthdate: null,
    country: null,
    gender: null,
    localId: null,
  },
  canGiveGifts: true,
  canMakePayments: false,
  canWrite: false,
  isRecurring: false,
  rate: 0,
  status: CommitmentStatusEnum.Closed,
  dates: { start: null, end: exit.effectiveDate },
});

/**
 * Resolves Final-only gift options for exited beneficiaries that aren't
 * present in the Active commitments list.
 *
 * Mirrors the Final branch of `formatGiftOptions` but is scoped to the
 * Final section so it can't pollute Birthday/Child/Family/Center.
 *
 * @param excludeGlobalIds - global IDs already represented in the Active
 *   commitments list. Anyone in this set is skipped to avoid duplicates.
 */
export const resolveExitedChildFinalOptions = (
  giftLimits: GraphQLSupporterGiftLimits | undefined,
  beneficiaryExits: ReadonlyArray<GraphQLSupporterBeneficiaryExit | null> = [],
  cartItems: CartItem[] = [],
  excludeGlobalIds: ReadonlySet<string> = new Set(),
): ChildGiftOption[] => {
  if (!giftLimits?.defaults.Final) {
    return [];
  }

  const finalDefaults = giftLimits.defaults.Final;
  const seenGlobalIds = new Set<string>();
  const results: ChildGiftOption[] = [];

  for (const exit of beneficiaryExits) {
    if (!exit) {
      continue;
    }

    const globalId = exit.exitedBeneficiary.globalId;
    if (
      !globalId ||
      excludeGlobalIds.has(globalId) ||
      seenGlobalIds.has(globalId)
    ) {
      continue;
    }

    const childGroup = giftLimits.children.find(
      (child) => child.id === globalId,
    );
    const finalLimits = childGroup?.giftTypes.Final;
    if (!childGroup || !finalLimits) {
      continue;
    }

    // Skip non-eligible Final entries the same way `resolveOption` does.
    // Empty string = eligible; `alreadyGiven` keeps an informational state.
    if (
      finalLimits.ineligibilityCode !== '' &&
      finalLimits.ineligibilityCode !== FinalGiftIneligibilityCode.alreadyGiven
    ) {
      continue;
    }

    const option = resolveGiftOption(
      GiftTypeEnum.Final,
      finalLimits,
      finalDefaults,
      childGroup.giftTypes,
      giftLimits.defaults,
    );

    const cartAware = applyChildCartAwareness(
      GiftTypeEnum.Final,
      option,
      cartItems,
      globalId,
      childGroup.giftTypes,
      giftLimits.defaults,
    );
    if (!cartAware) {
      continue;
    }

    seenGlobalIds.add(globalId);
    results.push({
      commitment: synthesizeExitedCommitment(exit),
      giftOption: cartAware,
    });
  }

  return results;
};
